import { useCallback, useEffect, useState } from "react";
import { UserProfile } from "../Entities/UserProfile";
import { CandidateResumeFilters, PaginationType } from "../Interface";
import userProfileService from "../Service/user.profile.service";

const defaultPagination: PaginationType = {
  page: 1,
  limit: 10,
  totalPages: 0,
  totalRecords: 0,
};

const defaultFilters: CandidateResumeFilters = {
  sort: null,
};

const useCandidateResumes = (candidateId: string) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [userProfiles, setUserProfiles] = useState<UserProfile[]>([]);
  const [pagination, setPagination] =
    useState<PaginationType>(defaultPagination);
  const [filters, setFilters] =
    useState<CandidateResumeFilters>(defaultFilters);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({
      ...prev,
      page: page,
    }));
  };

  const handleFilter = (key: keyof CandidateResumeFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handleClearFilter = () => {
    setFilters(defaultFilters);
    setPagination(defaultPagination);
  };

  const { sort } = filters;
  const { page, limit } = pagination;
  const getCandidateResumes = useCallback(async () => {
    if (!candidateId) {
      return;
    }
    try {
      setLoading(true);
      const candidateResumesResponse =
        await userProfileService.getAllCandidateProfilesByCandidateId(
          candidateId,
          page,
          limit,
          {
            sort,
          },
        );
      if (candidateResumesResponse) {
        setUserProfiles(candidateResumesResponse?.data || []);
        setPagination((prev) => ({
          ...prev,
          totalRecords: candidateResumesResponse.totalRecords || 0,
          totalPages: candidateResumesResponse.totalPages || 0,
        }));
      } else {
        console.error(
          "~ useCandidateResumes ~ Inavlid candidateResumesResponse:",
          candidateResumesResponse,
        );
        setUserProfiles([]);
        setPagination((prev) => ({
          ...prev,
          totalRecords: 0,
          totalPages: 0,
        }));
      }
    } catch (error) {
      console.error(
        "~ useCandidateResumes - getCandidateResumes ~ error:",
        error,
      );
      setUserProfiles([]);
      setPagination((prev) => ({
        ...prev,
        totalRecords: 0,
        totalPages: 0,
      }));
    } finally {
      setLoading(false);
    }
  }, [candidateId, setLoading, sort, page, limit]);

  useEffect(() => {
    getCandidateResumes();
  }, [getCandidateResumes]);

  return {
    loading,
    userProfiles,
    filters,
    pagination,
    handleClearFilter,
    handleFilter,
    handlePageChange,
  };
};

export default useCandidateResumes;
